/**
 * AllProductsPage - Full, entitlement-scoped product catalog (/all-products).
 *
 * Renders the same Products the sidebar derives (via `filterAppsForNavigation`)
 * as a searchable table/grid with A–Z (default) and pinned sort modes.
 * Pin state is persisted per-entity via usePinnedProducts (localStorage-backed,
 * designed to swap to a real API without touching this file).
 *
 * Gated behind the `psp-ui-all-products-page` flag at the route level
 * (see App.tsx). This component assumes it only renders when the flag is on.
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Stack, Typography } from '@mui/material';
import { NavBar } from '@opengov/components-nav-bar';
import { Result } from '@opengov/components-result';
import { usePspContext } from '../contexts/PspContext';
import { useEntity } from '../contexts/EntityContext';
import { filterAppsForNavigation } from '../services/navigationConfig';
import { isNewTabApp } from '../services/utils';
import { SearchIcon } from '../utils/icons';
import { MenuSectionTitle, type AppConfig } from '../types';
import { TestIds } from '../constants/testIds';
import { usePinnedProducts } from '../hooks/usePinnedProducts';
import { ProductTile } from './AllProducts/ProductTile';
import { ProductsTable } from './AllProducts/ProductsTable';
import { ProductsToolbar } from './AllProducts/ProductsToolbar';

export function AllProductsPage() {
  const navigate = useNavigate();
  const { appRegistry } = usePspContext();
  const { activeEntityEntitledApplications, activeEntityClaimKeys } = useEntity();

  const [query, setQuery] = useState('');
  const [sortMode, setSortMode] = useState<'az' | 'pinned'>('az');
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('table');
  // pinnedPaths is keyed by basePath, NOT id: multiple products (e.g. the
  // Financials sub-apps) share the same EntityApplicationProductKey id, so id
  // is not a unique identity. basePath is unique per app and is the nav target.
  const { pinnedPaths, togglePin, atPinLimit } = usePinnedProducts();

  function handleNavigate(app: AppConfig) {
    if (isNewTabApp(app)) {
      window.open(app.externalUrl, '_blank', 'noopener,noreferrer');
    } else {
      navigate(app.basePath);
    }
  }

  const productApps = useMemo(
    () =>
      filterAppsForNavigation(
        appRegistry,
        activeEntityEntitledApplications,
        activeEntityClaimKeys
      ).filter(
        (app) =>
          app.menuSection === MenuSectionTitle.PRODUCTS ||
          app.menuSection === MenuSectionTitle.CAPABILITIES
      ),
    [appRegistry, activeEntityEntitledApplications, activeEntityClaimKeys]
  );

  const visibleApps = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    const filtered = productApps.filter(
      (app) =>
        app.displayName.toLowerCase().includes(normalizedQuery) ||
        app.description?.toLowerCase().includes(normalizedQuery)
    );
    return filtered.sort((a, b) => {
      if (sortMode === 'pinned') {
        const aPinned = pinnedPaths.has(a.basePath);
        const bPinned = pinnedPaths.has(b.basePath);
        if (aPinned !== bPinned) return aPinned ? -1 : 1;
      }
      return a.displayName.localeCompare(b.displayName);
    });
  }, [productApps, query, sortMode, pinnedPaths]);

  return (
    <Stack sx={{ height: '100%', bgcolor: 'background.paper' }}>
      {/* NavBar renders appName as h2; this h1 satisfies WCAG 2.1 page heading */}
      <Typography
        variant="h1"
        component="h1"
        sx={{
          position: 'absolute',
          width: '1px',
          height: '1px',
          p: 0,
          m: '-1px',
          overflow: 'hidden',
          clip: 'rect(0, 0, 0, 0)',
          whiteSpace: 'nowrap',
          border: 0,
        }}
      >
        All Products
      </Typography>
      <NavBar appName="All Products" menuOptions={[]} />
      <Stack
        spacing={3}
        sx={{
          p: 4,
          flex: 1,
          width: '100%',
          maxWidth: 1520,
          mx: 'auto',
          bgcolor: 'background.paper',
        }}
      >
        <ProductsToolbar
          query={query}
          onQueryChange={setQuery}
          sortMode={sortMode}
          onSortChange={setSortMode}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
        />

        {visibleApps.length === 0 ? (
          <Result
            icon={<SearchIcon />}
            title="No products found"
            subTitle={
              query.trim() ? `No products match "${query.trim()}".` : 'No products available.'
            }
            status="empty"
            size="large"
          />
        ) : viewMode === 'table' ? (
          <ProductsTable
            apps={visibleApps}
            pinnedPaths={pinnedPaths}
            pinDisabled={atPinLimit}
            onTogglePin={togglePin}
            onNavigate={handleNavigate}
          />
        ) : (
          <Box
            data-testid={TestIds.ALL_PRODUCTS_GRID}
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(auto-fill, minmax(220px, 1fr))',
              },
              gap: 2,
            }}
          >
            {visibleApps.map((app) => (
              <ProductTile
                key={app.basePath}
                app={app}
                isPinned={pinnedPaths.has(app.basePath)}
                pinDisabled={atPinLimit}
                onTogglePin={togglePin}
                onNavigate={() => handleNavigate(app)}
              />
            ))}
          </Box>
        )}
      </Stack>
    </Stack>
  );
}
